import { create } from 'zustand';
import { bizDate, BUSINESS_TZ, enToBnDigits, formatInt, formatPercent, formatQty, formatTaka, unitLabel, type Lang } from '@dd/shared';

const KEY = 'dd.lang';
const initial = (): Lang => {
  try { const v = localStorage.getItem(KEY); if (v === 'bn' || v === 'en') return v; } catch { /* private mode */ }
  return 'bn';
};

interface LangState { lang: Lang; setLang: (l: Lang) => void }
export const useLang = create<LangState>((set) => ({
  lang: initial(),
  setLang: (lang) => {
    try { localStorage.setItem(KEY, lang); } catch { /* ignore */ }
    document.documentElement.lang = lang;
    set({ lang });
  },
}));
export const getLang = (): Lang => useLang.getState().lang;

/** Inline bilingual text: `t('বাকি খাতা', 'Baki')`. The component re-renders when the language changes. */
export function useT() {
  const lang = useLang((s) => s.lang);
  return {
    lang,
    t: (bn: string, en: string): string => (lang === 'bn' ? bn : en),
  };
}
export const tr = (bn: string, en: string): string => (getLang() === 'bn' ? bn : en);

// ── number & date formatting (always in the app language; digits Bangla in bn) ──────────────────────
export const taka = (v: string | number | null | undefined, o: { fixed?: boolean; symbol?: boolean } = {}): string => formatTaka(v ?? 0, { lang: getLang(), ...o });
export const num = (v: string | number | null | undefined): string => { const s = String(v ?? 0); return getLang() === 'bn' ? enToBnDigits(s) : s; };
export const intFmt = (n: number): string => formatInt(n, getLang());
export const qtyFmt = (v: string | number | null | undefined): string => formatQty(v ?? 0, getLang());
/** A unit key (pcs, kg, strip…) in the app language; a free-text pack name ("পাতা") is shown as typed. */
export const unitTxt = (u: string | null | undefined): string => unitLabel(u, getLang());
export const pct = (v: string | number): string => formatPercent(v, getLang());

const dtf = (opts: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(getLang() === 'bn' ? 'bn-BD' : 'en-GB', { timeZone: BUSINESS_TZ, ...opts });
export const fmtDate = (d: string | Date | number): string => {
  const date = typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(`${d}T12:00:00+06:00`) : new Date(d);
  return dtf({ day: 'numeric', month: 'short', year: 'numeric' }).format(date);
};
export const fmtDateShort = (d: string | Date | number): string => {
  const date = typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(`${d}T12:00:00+06:00`) : new Date(d);
  return dtf({ day: 'numeric', month: 'short' }).format(date);
};
export const fmtTime = (d: string | Date | number): string => dtf({ hour: 'numeric', minute: '2-digit', hour12: true }).format(new Date(d));
export const fmtDateTime = (d: string | Date | number): string => `${fmtDate(d)}, ${fmtTime(d)}`;
export const fmtMonth = (ym: string): string => dtf({ month: 'long', year: 'numeric' }).format(new Date(`${ym}-15T12:00:00+06:00`));

export function relDay(date: string): string {
  const today = bizDate(Date.now());
  const diff = Math.round((+new Date(`${today}T00:00:00Z`) - +new Date(`${date}T00:00:00Z`)) / 86_400_000);
  if (diff === 0) return tr('আজ', 'Today');
  if (diff === 1) return tr('গতকাল', 'Yesterday');
  return fmtDate(date);
}
